import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";

/**
 * The Rally Horn (MP-06, a pre-battle supply): one more squad of fortress troops for the first waves. Its slots are
 * added to the formation when the run is built and kept empty (TroopSystem.reserved); when wave 1 starts they open and
 * the squad musters at once (outside the spawn timer), the timer keeps them filled through wave `hornWaves`, and when a
 * later wave starts the squad's living troops stand down (TroopDied with no killer) and the slots close again. A run
 * without the horn never calls into this file, so it plays and hashes as it always did.
 */

/** When the run is built: the squad's slots, closed until the waves start. */
export function armRallyHorn(sim: LevelSim): void {
  const r = sim.mods.supplies;
  if (r === null || r.hornTroops <= 0 || r.hornWaves <= 0) return;
  sim.troops.reserve(r.hornTroops);
}

/** A stage began (fresh or joined to a running wave): the squad musters at wave 1 and stands down after its last. */
export function rallyHornAtStage(sim: LevelSim, stage: number): void {
  const r = sim.mods.supplies;
  if (r === null || r.hornTroops <= 0 || r.hornWaves <= 0) return;
  const troops = sim.troops;
  if (stage <= r.hornWaves && troops.reserved > 0) {
    troops.reserved = 0;
    for (let i = 0; i < r.hornTroops; i++) troops.muster();
    sim.events.add(SimEventKind.RallyHorn, r.hornTroops, 1);
  } else if (stage > r.hornWaves && troops.reserved === 0) {
    const from = troops.slots.length - r.hornTroops;
    for (let i = from; i < troops.slots.length; i++) {
      const t = troops.slots[i];
      if (t.alive) { t.alive = false; sim.events.add(SimEventKind.TroopDied, t.id, 0); }
    }
    troops.reserved = r.hornTroops;
    sim.events.add(SimEventKind.RallyHorn, r.hornTroops, 0);
  }
}

/** Whether the horn's squad stands now (the HUD's horn chip). */
export function rallyHornSounding(sim: LevelSim): boolean {
  const r = sim.mods.supplies;
  return r !== null && r.hornTroops > 0 && sim.stage >= 1 && sim.stage <= r.hornWaves && sim.troops.reserved === 0;
}
